// User-facing strings for the coach conversation, kept in one place so
// they can move to react-intl with the rest of Succeedo. The coach's name
// is per tenant (the conversation's coach_name); strings that mention the
// coach take it as an argument.

// Only used until the conversation (and its coach_name) has loaded.
export const DEFAULT_COACH_NAME = 'George';

export const COPY = {
  disclaimer: (coachName, brand) =>
    `${coachName} is an AI coach, not a financial, legal or tax advisor. Figures are estimates. ${brand || 'Succeedo'} AI can make mistakes.`,
  composerPlaceholder: (coachName) => `Message ${coachName}…`,
  // Before the owner's first message (D01).
  openingPlaceholder: (coachName) => `Tell ${coachName} in your own words…`,
  introEyebrow: (coachName, role) => (role ? `${coachName} · Your ${role.toLowerCase()}` : coachName),
  composerHint: 'Enter to send · Shift + Enter for a new line',
  thinking: (coachName) => `${coachName} is thinking`,
  error: (coachName) => `${coachName} couldn't reply just now. Your message is saved.`,
  retry: 'Try again',
  newMessage: 'New message',
  howWorkedOut: (coachName) => `How ${coachName} worked this out`,
  askAbout: (coachName) => `Ask ${coachName} about this`,
  openFullView: 'Open full view',
  saved: (date) => `Saved to your Workspace${date ? ` · ${date}` : ''}`,
  accept: 'Accept',
  notNow: 'Not now',
  inYourPlan: 'In your plan',
  scenarioEyebrow: 'What-if valuation',
  scenarioCompare: (value) => `Your current valuation: ${value}`,
  makeActive: 'Make this my valuation',
  makingActive: 'Saving…',
  nowActive: 'Now your valuation',
  scenarioHint: 'Saved as a scenario. Your numbers keep using your current valuation until you choose this one.',
  introduceMe: (name) => (name ? `Introduce me to ${name.split(' ')[0]}` : 'Introduce me'),
  introduceMessage: (name) => `Yes, please introduce me to ${name}.`,
  introductionRequested: (name) => `Introduction to ${name} requested`,
  partnerNotNow: (coachName) => `Not now. ${coachName} won't suggest this again for 30 days.`,
  confirmIntroTitle: (name) => `Introduce you to ${name || 'this partner'}?`,
  whatTheyReceive: (name) => `What ${name ? name.split(' ')[0] : 'they'} will receive`,
  resultsToShare: 'Saved results to share (untick any you want to keep private)',
  briefLabel: (name) => `Send ${name || 'the partner'} a short brief about you`,
  briefHint: 'Drafted from what you allow us to share. Edit it however you like.',
  briefCannotRecall: 'A sent brief cannot be recalled. If you stop sharing later, partners stop seeing it, but they may already have read it.',
  nothingSharedYet: 'Nothing is shared until you confirm. Partners are paid members of the network; who we suggest is based on fit, never on fees.',
  confirmIntro: 'Yes, introduce me',
  sending: 'Sending…',
  introductionSent: (name) => `Introduction sent to ${name || 'the partner'}. You'll get an email when they reply.`,
  introductionDeclined: 'You chose not to go ahead. Nothing was shared.',
  confirmationError: "That didn't go through. Please try again.",
  // Succeedo Phase 5: a brief to the owner's own advisor.
  advisorBriefTitle: (name) => `Send ${name || 'your advisor'} a brief?`,
  advisorBriefAbout: (topic) => `About: ${topic}`,
  advisorBriefHint: 'This is exactly what they will read. Edit it however you like; leave out anything personal.',
  advisorBriefPrivacy: 'Your advisor sees only this brief, never your conversations with George. A sent brief cannot be recalled.',
  advisorBriefSend: (name) => `Send to ${name || 'my advisor'}`,
  advisorBriefSent: (name) => `Sent to ${name || 'your advisor'}. It's also in your plan as your next step.`,
  advisorBriefDeclined: 'You chose not to send it. Nothing was shared.',
  helpful: 'Helpful',
  notHelpful: 'Not helpful',
  breadcrumb: (coachName) => (coachName ? `${coachName.toUpperCase()} · CONVERSATION` : 'CONVERSATION'),
  suggestedHeading: (coachName) => `Suggested by ${coachName}`,
  suggestedHint: 'The steps you accept become your 90-day plan.',
  suggestedSummary: (coachName, count) => `${coachName} suggested ${count} ${count === 1 ? 'step' : 'steps'}`,
  suggestedDone: 'All reviewed',
  reviewInActions: 'Review in Actions',
  andMore: (count) => `and ${count} more`,
  actionsEmptyHeading: 'Your next steps will live here.',
  actionsEmpty: (coachName) =>
    `As we talk, ${coachName} will suggest a few concrete steps. The ones you accept appear here with what they're worth, and become your 90-day plan.`,
  workspaceHeading: (coachName) => `Everything you and ${coachName} have worked out`,
  talkTo: (coachName) => `Talk to ${coachName}`,
  workspaceEmptyHeading: (coachName) => `Start with ${coachName}`,
  workspaceEmpty: (coachName) => `Nothing saved yet. Ask ${coachName} "am I going to be OK?" and your results will appear here.`,
  workspaceEmptyTeam: 'Or add the advisors you already work with',
  workspaceLoading: 'Loading your saved results…',
  workspaceError: "Your saved results couldn't load. Please refresh the page.",
};

export const VERDICTS = {
  on_track: { label: 'On track', color: 'hsl(var(--chart-2))', ink: 'hsl(var(--teal-ink))', tint: 'hsl(var(--chart-2) / 0.12)' },
  close: { label: 'Close, with work', color: 'hsl(var(--amber))', ink: 'hsl(var(--amber-ink))', tint: 'hsl(var(--amber) / 0.14)' },
  not_yet: { label: 'Not yet', color: 'hsl(var(--coral))', ink: 'hsl(var(--coral-ink))', tint: 'hsl(var(--coral) / 0.14)' },
};

export const ARTIFACT_NAMES = {
  wealth_goal: 'Wealth goal',
  valuation_range: 'Valuation range',
  am_i_ok: 'Am I OK? · Wealth picture',
  readiness_scorecard: 'Readiness scorecard',
  value_gap: 'Value gap',
  financing_scenario: 'Financing scenario',
  deal_structure: 'Deal structure',
  partner_recommendation: 'Partner suggestion',
};

// Where "Open full view" goes for each artifact kind (existing tools).
export const FULL_VIEW_PATHS = {
  valuation_range: '/succeedo/valuation',
  value_gap: '/succeedo/valuation',
  readiness_scorecard: '/succeedo/assessment',
  financing_scenario: '/succeedo/financing-calculator',
  deal_structure: '/succeedo/deal-structure',
  partner_recommendation: (artifact) => (artifact.data?.partner?.id ? `/succeedo/partners/${artifact.data.partner.id}` : null),
};

// Partner categories (PartnerCategorizable), as owners read them.
export const PARTNER_CATEGORY_LABELS = {
  cbv: 'Business valuator (CBV)',
  business_broker: 'Business broker',
  lender: 'Lender',
  cepa_coach: 'Exit planning coach (CEPA)',
  accountant: 'Accountant',
  lawyer: 'Lawyer',
};

export const CATEGORY_LABELS = {
  financial: 'Financial',
  human: 'Human capital',
  structural: 'Structural',
  customer: 'Customer',
  wealth: 'Personal wealth',
};

// Background class for each category's dot or bar (chart tokens).
const CATEGORY_BG = {
  financial: 'bg-[hsl(var(--chart-1))]',
  human: 'bg-[hsl(var(--chart-2))]',
  structural: 'bg-[hsl(var(--chart-3))]',
  customer: 'bg-[hsl(var(--chart-4))]',
  wealth: 'bg-[hsl(var(--chart-5))]',
};

export const categoryBg = (category) => CATEGORY_BG[category] || 'bg-succeedo-orange';
